/**
 * Single source of truth for "what is the workbench preview doing right now, and what
 * should we tell the user" — merges what used to be two independently-computed pieces of
 * `Workbench.tsx` (a priority-ordered ladder, plus a separate hardcoded override for the
 * iframe/bridge wait) into one ordered derivation. Pure — no React, no store reads — so
 * `Workbench.tsx` stays the only place that reads store atoms and passes their values in.
 */

export type WorkbenchLoadingPhase =
  | 'installing-deps'
  | 'connecting-fast-preview'
  | 'building'
  | 'applying-changes'
  | 'generating'
  | 'build-error'
  | 'reconnecting-snapshot'
  | 'continuing-build'
  | 'waiting-for-app-mount'
  | 'app-crashed'
  | 'version-not-compiling'
  | 'no-saved-version'
  | 'idle-empty'
  | 'ready';

export interface WorkbenchLoadingPhaseInput {
  depsInstalled: boolean;
  buildStatus: 'idle' | 'building' | 'success' | 'error';
  isStreaming: boolean;
  clientToolsBusy: boolean;
  previewUrl: string;
  snapshotPreviewReady: boolean;
  preferFastDevPreview: boolean;
  /** Whether the preview iframe is currently allowed to be shown at all. */
  previewInteractive: boolean;
  /** Which condition made `previewInteractive` true — lets the app-mount wait continue
   * whatever phrase was already showing instead of jumping to an unrelated one. */
  previewInteractiveReason: 'success' | 'snapshot' | 'fast-dev' | null;
  previewIframeLoaded: boolean;
  bridgeReady: boolean;
  bridgeReadyTimedOut: boolean;
  /** The iframe reported `IDEOZ_EDIT_CRASHED` and has not rendered since. */
  previewCrashed?: boolean;
  /** What rendered is the starter `App.tsx`, not a generated prototype. */
  previewShowsTemplatePlaceholder?: boolean;
  /** A saved version is still being looked up or restored, so the starter page proves nothing. */
  snapshotRestorePending?: boolean;
  /** Continue was pressed on an unfinished build that has not started building yet. */
  continuingBuild?: boolean;
  /**
   * The Vite dev server is up (`devServerStatus === 'ready'`). False while a restore or restart
   * has it stopped; nothing from the previous page counts as rendered until it is back.
   */
  previewServerReady?: boolean;
  /**
   * The light preview's last compile of the workspace produced no page, and no compile is
   * running, so the iframe shows an earlier set of files.
   */
  previewCompileFailed?: boolean;
  /** A hand edit since the last build: its compile errors belong to the edit, not the version. */
  hasUnbuiltEdits?: boolean;
}

export interface WorkbenchLoadingPhaseResult {
  phase: WorkbenchLoadingPhase;
  message: string;
  variant: 'generating' | 'plain';
  /** True once there's actually something interactive on screen. */
  isReady: boolean;
}

export function deriveWorkbenchLoadingPhase(
  input: WorkbenchLoadingPhaseInput,
): WorkbenchLoadingPhaseResult {
  const {
    depsInstalled,
    buildStatus,
    isStreaming,
    clientToolsBusy,
    previewUrl,
    snapshotPreviewReady,
    preferFastDevPreview,
    previewInteractive,
    previewInteractiveReason,
    previewIframeLoaded,
    bridgeReady,
    bridgeReadyTimedOut,
    previewCrashed = false,
    previewShowsTemplatePlaceholder = false,
    snapshotRestorePending = false,
    continuingBuild = false,
    previewServerReady = true,
    previewCompileFailed = false,
    hasUnbuiltEdits = false,
  } = input;

  if (!depsInstalled) {
    return { phase: 'installing-deps', message: 'Setting up your prototype workspace…', variant: 'plain', isReady: false };
  }

  if (preferFastDevPreview && previewUrl && buildStatus !== 'success' && buildStatus !== 'error') {
    return { phase: 'connecting-fast-preview', message: 'Connecting to your live preview…', variant: 'plain', isReady: false };
  }

  if (buildStatus === 'building') {
    return { phase: 'building', message: 'Building and checking your prototype…', variant: 'plain', isReady: false };
  }

  if (clientToolsBusy) {
    return { phase: 'applying-changes', message: 'Applying the latest changes…', variant: 'plain', isReady: false };
  }

  // The preview waits for the turn, not for its first passing build: a mobile build is sent back
  // to fix design rules after it passes, so showing it then flashed the app and hid it again
  // for the next build (1 Oct 2026).
  if (isStreaming) {
    const message = buildStatus === 'success' ? 'Finishing your prototype…' : 'Generating your prototype…';
    return { phase: 'generating', message, variant: 'generating', isReady: false };
  }

  if (buildStatus === 'error') {
    return {
      phase: 'build-error',
      message: 'The last build hit an error — check the Terminal, then Rebuild to try again.',
      variant: 'plain',
      isReady: false,
    };
  }

  // Ahead of the snapshot and app-mount waits: a crashed app never mounts, so waiting on it
  // would show a spinner (or, after the 15s escape hatch, a blank iframe) instead of the error.
  if (previewInteractive && previewServerReady && previewCrashed) {
    return { phase: 'app-crashed', message: 'This screen crashed', variant: 'plain', isReady: false };
  }

  // Continue on an unfinished build: the starter page (or a blank preview) is only what the
  // workspace holds before the build starts. Ahead of the empty state, which it used to fall into.
  if (continuingBuild && (previewShowsTemplatePlaceholder || !previewInteractive)) {
    return {
      phase: 'continuing-build',
      message: 'Finishing this build…',
      variant: 'plain',
      isReady: false,
    };
  }

  // Until the restore settles, the starter page is just what an empty workspace looks like —
  // announcing "no saved version" there was wrong for the second it takes to find out, and
  // flashed that empty state on every refresh into the prototyper. The same wait covers a
  // preview that has not published anything yet, which is what the light preview does while it
  // holds its first compile for exactly this reason.
  if (snapshotRestorePending && (previewShowsTemplatePlaceholder || !previewInteractive)) {
    return {
      phase: 'reconnecting-snapshot',
      message: 'Reconnecting to your saved prototype…',
      variant: 'plain',
      isReady: false,
    };
  }

  // The version's own files do not compile in the tab, so the page on screen is an earlier one:
  // the starter after a reload, which read as "No saved version" (Bug 893), or the version
  // picked before, under this version's name. A hand edit's typo keeps the last working page
  // instead; its errors are about the edit.
  if (previewCompileFailed && !hasUnbuiltEdits) {
    return {
      phase: 'version-not-compiling',
      message: "This version can't be shown in the preview",
      variant: 'plain',
      isReady: false,
    };
  }

  // The starter page is not a prototype (SN-4). Generating / building phases above still win,
  // so a first build that briefly renders the starter keeps its own progress message.
  if (previewInteractive && previewServerReady && previewShowsTemplatePlaceholder) {
    return {
      phase: 'no-saved-version',
      message: 'No saved version of this prototype yet',
      variant: 'plain',
      isReady: false,
    };
  }

  if (snapshotPreviewReady && previewUrl) {
    // `snapshotPreviewReady` stays true for the rest of the session once a restored/old
    // prototype is shown (it's only cleared by a full template reset or a fresh rebuild) —
    // so this can't unconditionally report "not ready" the way it used to, or viewing any
    // restored prototype leaves `isReady` (and therefore Publish) stuck disabled forever even
    // though the iframe is already showing the working app. Fall back to the same
    // bridge-confirmed-mount signal (with the same 15s timeout escape hatch) the live-build
    // path uses below, instead of a flag that never resets.
    // While a restore has the dev server stopped, the previous page's signals do not count.
    const mounted = previewServerReady && (bridgeReady || bridgeReadyTimedOut);
    return {
      phase: 'reconnecting-snapshot',
      message: 'Reconnecting to your saved prototype…',
      variant: 'plain',
      isReady: mounted,
    };
  }

  // App has been given the go-ahead to show (build succeeded / snapshot restored / fast dev
  // preview), but the iframe hasn't finished loading or the app hasn't mounted inside it yet.
  // Continue whatever phrase already applied to `previewInteractive` becoming true instead of
  // jumping to an unrelated one — the one genuinely new wait is the post-build-success case,
  // where nothing earlier in this ladder has said anything about the preview yet.
  //
  // `bridgeReadyTimedOut` is the 15s escape hatch: once it fires we stop waiting on EITHER
  // signal, not just the bridge — a dev server that never fires the iframe's native `load`
  // event at all (a stalled network layer, not a broken generated app) must still eventually
  // give up and show the iframe, or this phase never exits.
  if (
    previewInteractive &&
    (!previewServerReady || (!bridgeReadyTimedOut && (!previewIframeLoaded || !bridgeReady)))
  ) {
    const message =
      previewInteractiveReason === 'fast-dev'
        ? 'Connecting to your live preview…'
        : previewInteractiveReason === 'snapshot'
          ? 'Reconnecting to your saved prototype…'
          : 'Loading your prototype preview…';
    return { phase: 'waiting-for-app-mount', message, variant: 'plain', isReady: false };
  }

  if (preferFastDevPreview && previewUrl) {
    return { phase: 'connecting-fast-preview', message: 'Connecting to your live preview…', variant: 'plain', isReady: false };
  }

  if (!previewInteractive) {
    return {
      phase: 'idle-empty',
      message: "Your prototype preview will appear here once it's ready.",
      variant: 'plain',
      isReady: false,
    };
  }

  return { phase: 'ready', message: '', variant: 'plain', isReady: true };
}
